import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Skeleton,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { formatPrice } from '../lib/format.js';
import { queryKeys } from '../lib/queries.js';

/** Green above 75, amber above 50, plain below — the eye sorts before the number does. */
function scoreTone(score: number): 'success' | 'warning' | 'neutral' {
  if (score >= 75) return 'success';
  if (score >= 50) return 'warning';
  return 'neutral';
}

/**
 * The matching workbench (docs/domain/07-demands-and-matching.md).
 *
 * It is recomputed on every visit rather than read from the stored matches —
 * those exist for notification dedupe, and showing an agent a pairing that
 * stopped being true is worse than showing none.
 *
 * Each row carries its reasons, because an agent has to be able to say why
 * before they call a client about it.
 */
export function DemandMatches({ demandId }: { demandId: string }) {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.demandMatches(demandId),
    queryFn: async ({ signal }) =>
      assertOk(
        await api.demands.matches({
          params: { id: demandId },
          query: { limit: 25 },
          fetchOptions: { signal },
        }),
      ).body,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Immobili compatibili</CardTitle>
        <CardDescription>
          Solo immobili con incarico attivo e pubblicato. Ricalcolati a ogni apertura.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isPending && <Skeleton className="h-20 w-full" />}

        {isError && <ErrorState onRetry={() => void refetch()} />}

        {!isPending && !isError && data.length === 0 && (
          <p className="text-secondary text-muted">
            Nessun immobile compatibile. Allarga il budget, l’area o i requisiti minimi.
          </p>
        )}

        <ul className="grid gap-2">
          {data?.map((match) => (
            <li key={match.propertyId}>
              <Link
                to="/immobili/$id"
                params={{ id: match.propertyId }}
                className="grid gap-1 rounded-control border border-border px-3 py-2 hover:bg-surface-sunken"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={scoreTone(match.score)}>{match.score}</Badge>
                  <span className="text-body text-ink">{match.propertyLabel}</span>
                  <span className="ml-auto text-body text-ink">{formatPrice(match.price)}</span>
                </div>
                <p className="text-secondary text-muted">
                  {[
                    match.surfaceMq ? `${match.surfaceMq} mq` : null,
                    match.locali ? `${match.locali} locali` : null,
                    match.zonaName ?? match.comuneName,
                    ...match.reasons,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * The same ranking read from the other side: whose search does this property
 * answer? Only live demands appear — a lost one is history.
 */
export function PropertyDemands({ propertyId }: { propertyId: string }) {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.propertyDemands(propertyId),
    queryFn: async ({ signal }) =>
      assertOk(
        await api.propertyMatches.interestedDemands({
          params: { id: propertyId },
          query: { limit: 25 },
          fetchOptions: { signal },
        }),
      ).body,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Richieste interessate</CardTitle>
        <CardDescription>
          Visibili solo se l’immobile ha un incarico attivo e pubblicato.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isPending && <Skeleton className="h-16 w-full" />}

        {isError && <ErrorState onRetry={() => void refetch()} />}

        {!isPending && !isError && data.length === 0 && (
          <p className="text-secondary text-muted">Nessuna richiesta compatibile.</p>
        )}

        <ul className="grid gap-2">
          {data?.map((match) => (
            <li key={match.demandId}>
              <Link
                to="/richieste/$id"
                params={{ id: match.demandId }}
                className="flex flex-wrap items-center gap-3 rounded-control border border-border px-3 py-2 hover:bg-surface-sunken"
              >
                <Badge tone={scoreTone(match.score)}>{match.score}</Badge>
                <span className="text-body text-ink">{match.contactName}</span>
                <span className="text-secondary text-muted">{match.reasons.join(' · ')}</span>
                <span className="ml-auto text-body text-ink">{formatPrice(match.budgetMax)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
